import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import { CheckCircle, XCircle, ArrowUp, Search, Eye } from 'lucide-react';

const STATUSES = ['pending', 'approved', 'rejected', 'all'];

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const tipAmount = (req) => Number(req?.tip_amount || 0);
const walletDebitAmount = (req) => {
  const explicit = Number(req?.wallet_debit_amount || req?.requested_amount || 0);
  if (explicit > 0) return explicit;
  return Number(req?.amount || 0) + tipAmount(req);
};

export default function WithdrawalRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchRequests(); }, [status, search]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `/api/admin/transactions/withdrawals?limit=100&status=${status === 'all' ? '' : status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await api.get(url);
      setRequests(res.data.items || []);
    } catch { toast.error('Failed to load withdrawals'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/approve`, { admin_notes: adminNotes });
      toast.success('Withdrawal approved!');
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const handleReject = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/reject`, { reason: adminNotes });
      toast.success('Withdrawal rejected');
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="withdrawal-requests-page">
      <h2 className="text-xl font-semibold flex items-center gap-2"><ArrowUp className="w-5 h-5 text-red-400" /> Withdrawal Requests
        {requests.filter(r => r.status === 'pending').length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">{requests.filter(r => r.status === 'pending').length} pending</span>}
      </h2>
      <div className="flex gap-2 flex-wrap">
        <div className="flex rounded-lg p-0.5 bg-[#0B0E14]">
          {STATUSES.map(s => <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition ${status === s ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>{s}</button>)}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by username or email..." className="pl-10 bg-[#0B0E14] border-white/10" />
        </div>
      </div>
      {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : requests.length === 0 ? <p className="text-gray-500 py-8 text-center">No withdrawal requests</p> : (
        <div className="space-y-2">
          {requests.map(req => {
            const tip = tipAmount(req);
            const hasTip = tip > 0;
            return (
            <div key={req.id} className="glass-card rounded-xl p-4 flex items-center justify-between" data-testid={`withdrawal-${req.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0"><ArrowUp className="w-5 h-5 text-red-400" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-white">{req.username || 'Unknown'}</p>
                  <p className="text-[10px] text-gray-500">{req.email} | {req.payment_method_name || 'N/A'} | {new Date(req.created_at).toLocaleString()}</p>
                  {hasTip && (
                    <p className="mt-1 text-[10px] font-semibold text-yellow-300">
                      Tip {money(tip)} to {req.tip_agent_name || 'agent'} · wallet deducted {money(walletDebitAmount(req))}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-3">
                <div className="text-right">
                  <p className="text-lg font-bold text-red-400">{money(req.amount)}</p>
                  {hasTip && <p className="text-[10px] text-gray-400">cashout after tip</p>}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${req.status === 'approved' ? 'bg-green-500/20 text-green-400' : req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{req.status}</span>
                </div>
                {req.status === 'pending' && (
                  <div className="flex gap-2 ml-2">
                    <button onClick={() => { setSelected(req); setAdminNotes(''); }} className="px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-medium flex items-center gap-1 hover:bg-blue-500/20 transition">
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    <button onClick={() => handleApprove(req.id)} className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-medium flex items-center gap-1 hover:bg-green-500/20 transition">
                      <CheckCircle className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button onClick={() => handleReject(req.id)} className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium flex items-center gap-1 hover:bg-red-500/20 transition">
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
      )}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Withdrawal Details" maxWidth="max-w-md">
        {selected && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[10px] text-gray-500">User</p><p className="font-medium">{selected.username}</p></div>
              <div><p className="text-[10px] text-gray-500">Cashout Amount</p><p className="font-bold text-red-400">{money(selected.amount)}</p></div>
              <div><p className="text-[10px] text-gray-500">Email</p><p>{selected.email}</p></div>
              <div><p className="text-[10px] text-gray-500">Method</p><p>{selected.payment_method_name || 'N/A'}</p></div>
            </div>
            {tipAmount(selected) > 0 && (
              <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-yellow-200">Agent Tip</span>
                  <span className="font-bold text-yellow-300">{money(tipAmount(selected))}</span>
                </div>
                <div className="mt-1 flex justify-between gap-3">
                  <span className="text-gray-400">Favorite Agent</span>
                  <span className="font-semibold text-white">{selected.tip_agent_name || 'Agent'}</span>
                </div>
                <div className="mt-1 flex justify-between gap-3">
                  <span className="text-gray-400">Wallet Deducted</span>
                  <span className="font-semibold text-white">{money(walletDebitAmount(selected))}</span>
                </div>
              </div>
            )}
            {selected.payout_details && <div><p className="text-[10px] text-gray-500">Payout Details</p><pre className="text-xs bg-[#0B0E14] p-2 rounded-lg">{typeof selected.payout_details === 'string' ? selected.payout_details : JSON.stringify(selected.payout_details, null, 2)}</pre></div>}
            {selected.status === 'pending' && <>
              <Input value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Admin notes" className="bg-[#0B0E14] border-white/10" />
              <div className="flex gap-2">
                <Button onClick={() => handleApprove(selected.id)} disabled={processing} className="flex-1 bg-green-600">Approve</Button>
                <Button onClick={() => handleReject(selected.id)} disabled={processing} className="flex-1 bg-red-600">Reject</Button>
              </div>
            </>}
          </div>
        )}
      </Modal>
    </div>
  );
}
